import { useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  onConnect: (token: string) => Promise<boolean>;
}

export function ReadwiseConnectDialog({ open, onOpenChange, busy, onConnect }: Props) {
  const [token, setToken] = useState("");

  const handleConnect = async () => {
    if (!token.trim()) {
      toast.error("Paste your Readwise token first");
      return;
    }
    const ok = await onConnect(token.trim());
    if (ok) {
      setToken("");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Readwise</DialogTitle>
          <DialogDescription>
            Paste your Readwise access token below. Your token is encrypted before being stored.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <a
            href="https://readwise.io/access_token"
            target="_blank"
            rel="noreferrer"
            className="text-primary text-sm inline-flex items-center gap-1 hover:underline"
          >
            Get your token from readwise.io
            <ExternalLink className="h-3 w-3" />
          </a>
          <div>
            <Label>Readwise access token</Label>
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste token here"
              autoComplete="off"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleConnect} disabled={busy || !token.trim()}>
            {busy ? "Connecting…" : "Connect & sync"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
